// Typed client surface for the `billing` schema.
// All writes flow through SECURITY DEFINER RPCs. Reads either come from the
// rpc_admin_get_org_billing aggregate or from direct table selects guarded
// by RLS.

import { supabase } from '../client';

// ---- enums and shared types ---------------------------------------------

export type PlanKind = 'beginner' | 'pro' | 'expert' | 'enterprise';
export type BillingCycle = 'monthly' | 'yearly';
export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'unpaid'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired';
export type SubscriptionItemKind = 'plan' | 'module';
export type PriceOverrideTarget = 'plan' | 'module';
export type PriceOverrideKind = 'percent' | 'fixed';
export type InvoiceStatus = 'draft' | 'open' | 'paid' | 'uncollectible' | 'void';
export type PaymentStatus =
  | 'succeeded'
  | 'pending'
  | 'failed'
  | 'refunded'
  | 'partially_refunded';
// QuotaKind is now an open string ('clients' | 'emails' | 'invoices' |
// 'seats' | 'stores' | <any future key the admin adds via the registry>).
// Frontend code that needs to enumerate kinds must read them at runtime
// from listQuotaKinds() rather than from a closed compile-time union.
export type QuotaKind = string;

export interface QuotaKindRow {
  key: string;
  label: string;
  unit: string;
  period: 'lifetime' | 'period';
  measure_kind: 'live_clients' | 'live_stores' | 'live_seats' | 'counter';
  description: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PlanQuotaCap {
  quota_kind: string;
  cap: number;
}

export interface PlanRow {
  id: string;
  slug: string;
  name: string;
  kind: PlanKind;
  org_id: string | null;
  is_active: boolean;
  display_order: number;
  monthly_unit_amount_cents: number;
  yearly_unit_amount_cents: number;
  currency: string;
  stripe_product_id: string | null;
  monthly_stripe_price_id: string | null;
  yearly_stripe_price_id: string | null;
  trial_days: number;
  description: string | null;
  created_at: string;
  updated_at: string;
  /** Caps for each quota the registry exposes. Driven by billing.plan_quotas. */
  quotas: PlanQuotaCap[];
}

export interface PlanRowWithSubscriberCount extends PlanRow {
  subscriber_count: number;
}

export interface ModulePriceRow {
  module_id: string;
  name: string;
  monthly_unit_amount_cents: number;
  yearly_unit_amount_cents: number;
  currency: string;
  stripe_product_id: string | null;
  monthly_stripe_price_id: string | null;
  yearly_stripe_price_id: string | null;
  is_active: boolean;
  /**
   * Always-on entitlement. When true, billing.fn_sync_org_module_flags
   * grants this module to every org regardless of subscription state.
   * Set from the Admin app via `adminSetModuleIsFree`.
   */
  is_free: boolean;
  display_order: number;
  description: string | null;
  created_at: string;
  updated_at: string;
}

/** Row shape returned by `billing.rpc_admin_list_plan_modules`. */
export interface PlanModuleRow {
  module_id: string;
  name: string;
  display_order: number;
  is_free: boolean;
  is_active: boolean;
  included: boolean;
}

export interface OrgSubscriptionRow {
  id: string;
  org_id: string;
  plan_id: string;
  billing_cycle: BillingCycle;
  status: SubscriptionStatus;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  trial_start: string | null;
  trial_end: string | null;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  default_payment_method: string | null;
  latest_invoice_id: string | null;
  past_due_since: string | null;
  /**
   * Platform-admin-only flag (see migration
   * 20260628100000_billing_dev_paid_override.sql). When true, every gate
   * that asks "is this org paid?" treats the row as if `status='active'`,
   * letting internal QA orgs exercise paid features without putting real
   * money through Stripe. Survives Stripe webhook syncs and the
   * trial-expiry cron.
   */
  dev_paid_override: boolean;
  dev_paid_override_reason: string | null;
  dev_paid_override_set_at: string | null;
  dev_paid_override_set_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrgSubscriptionItemRow {
  id: string;
  subscription_id: string;
  kind: SubscriptionItemKind;
  module_id: string | null;
  stripe_subscription_item_id: string | null;
  stripe_price_id: string | null;
  quantity: number;
  effective_unit_amount_cents: number | null;
  created_at: string;
  updated_at: string;
}

export interface OrgPriceOverrideRow {
  id: string;
  org_id: string;
  target_kind: PriceOverrideTarget;
  module_id: string | null;
  discount_kind: PriceOverrideKind;
  percent_off: number | null;
  custom_monthly_unit_amount_cents: number | null;
  custom_yearly_unit_amount_cents: number | null;
  stripe_coupon_id: string | null;
  stripe_custom_monthly_price_id: string | null;
  stripe_custom_yearly_price_id: string | null;
  effective_from: string;
  effective_until: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface EntitlementOverrideRow {
  id: string;
  org_id: string;
  module_id: string;
  enabled: boolean;
  reason: string | null;
  granted_by: string | null;
  granted_at: string;
  expires_at: string | null;
}

export interface QuotaOverrideRow {
  id: string;
  org_id: string;
  quota_kind: QuotaKind;
  value: number;
  reason: string | null;
  granted_by: string | null;
  granted_at: string;
  expires_at: string | null;
}

export interface UsageCounterRow {
  org_id: string;
  quota_kind: string;
  current_value: number;
  period_start: string | null;
  period_end: string | null;
  last_recomputed_at: string;
}

/** Map of quota_kind -> current usage, derived from billing.usage_counters. */
export type UsageMap = Record<string, number>;
/** Map of quota_kind -> effective cap (plan_quotas + override). */
export type CapMap = Record<string, number>;

export interface InvoiceRow {
  id: string;
  org_id: string;
  stripe_invoice_id: string;
  status: InvoiceStatus;
  number: string | null;
  currency: string;
  amount_due_cents: number;
  amount_paid_cents: number;
  amount_remaining_cents: number;
  subtotal_cents: number;
  total_cents: number;
  tax_cents: number;
  period_start: string | null;
  period_end: string | null;
  hosted_invoice_url: string | null;
  invoice_pdf_url: string | null;
  paid_at: string | null;
  due_date: string | null;
  voided_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaymentRow {
  id: string;
  org_id: string;
  invoice_id: string | null;
  stripe_payment_intent_id: string | null;
  stripe_charge_id: string | null;
  amount_cents: number;
  currency: string;
  status: PaymentStatus;
  failure_reason: string | null;
  paid_at: string | null;
  created_at: string;
}

export interface OrgBillingSnapshot {
  subscription: OrgSubscriptionRow | null;
  plan: PlanRow | null;
  items: OrgSubscriptionItemRow[];
  price_overrides: OrgPriceOverrideRow[];
  quota_overrides: QuotaOverrideRow[];
  entitlement_overrides: EntitlementOverrideRow[];
  /** Active rows from billing.quota_kinds, in display_order. */
  quota_kinds: QuotaKindRow[];
  /** Effective cap per quota_kind (plan cap, replaced by active override). */
  caps: CapMap;
  /** Current value per quota_kind. */
  usage: UsageMap;
  invoices: InvoiceRow[];
  payments: PaymentRow[];
}

export interface AdminSubscriptionRow {
  org_id: string;
  org_name: string;
  org_slug: string;
  plan_id: string | null;
  plan_name: string | null;
  plan_kind: PlanKind | null;
  billing_cycle: BillingCycle | null;
  status: SubscriptionStatus | null;
  current_period_end: string | null;
  trial_end: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  active_module_count: number;
  effective_mrr_cents: number;
  past_due_since: string | null;
}

// ---- public-catalog reads (any authenticated user) ----------------------

export async function listPublishedPlans(): Promise<PlanRow[]> {
  const { data, error } = await supabase()
    .schema('billing')
    .from('plans')
    .select('*')
    .is('org_id', null)
    .eq('is_active', true)
    .order('display_order')
    .limit(200);
  if (error) throw error;
  return (data ?? []) as unknown as PlanRow[];
}

export async function listModulePricesPublic(): Promise<ModulePriceRow[]> {
  const { data, error } = await supabase()
    .schema('billing')
    .from('module_prices')
    .select('*')
    .eq('is_active', true)
    .order('display_order')
    .limit(500);
  if (error) throw error;
  return (data ?? []) as ModulePriceRow[];
}

/**
 * Returns the set of module IDs included in the given plan. Read-only,
 * authenticated. Used by `apps/web/src/settings/SettingsBillingPage.tsx`
 * to render plan-included modules as locked-on checkboxes (the operator
 * cannot un-tick a module the plan already grants — the toggle would
 * be a no-op because `billing.fn_sync_org_module_flags` ORs the plan
 * set into the eligibility check, and we'd just bill them twice if
 * the redundant module also got added as a paid Stripe line item).
 *
 * Backed by `billing.fn_plan_modules(uuid)` (granted to authenticated
 * via 20260522000000), which already encodes the enterprise-default
 * fallback when a plan has no `billing.plan_modules` rows yet.
 *
 * Pass `null` to get an empty array (defensive for the bootstrap-only
 * case where the operator has no subscription row yet).
 */
export async function listPlanModuleIds(planId: string | null | undefined): Promise<string[]> {
  if (!planId) return [];
  const { data, error } = await supabase()
    .schema('billing')
    .rpc('fn_plan_modules', { p_plan_id: planId });
  if (error) throw error;
  if (!data) return [];
  if (Array.isArray(data)) return data as string[];
  return [];
}

// ---- platform-admin RPCs -------------------------------------------------

export async function adminListPlans(): Promise<PlanRowWithSubscriberCount[]> {
  const { data, error } = await supabase().schema('billing').rpc('rpc_admin_list_plans');
  if (error) throw error;
  return (data ?? []) as unknown as PlanRowWithSubscriberCount[];
}

export interface UpsertPlanInput {
  id?: string;
  slug?: string;
  name?: string;
  kind?: PlanKind;
  orgId?: string | null;
  monthlyUnitAmountCents?: number;
  yearlyUnitAmountCents?: number;
  /**
   * Full set of caps for this plan. Omitted quota_kind keys are deleted
   * from billing.plan_quotas. Pass undefined to leave quotas unchanged.
   */
  quotas?: PlanQuotaCap[];
  trialDays?: number;
  description?: string | null;
  isActive?: boolean;
  archiveReason?: string | null;
}

export async function adminUpsertPlan(input: UpsertPlanInput): Promise<string> {
  const { data, error } = await supabase().schema('billing').rpc('rpc_admin_upsert_plan', {
    p_id: input.id ?? undefined,
    p_slug: input.slug ?? undefined,
    p_name: input.name ?? undefined,
    p_kind: input.kind ?? undefined,
    p_org_id: input.orgId ?? undefined,
    p_monthly_unit_amount_cents: input.monthlyUnitAmountCents ?? undefined,
    p_yearly_unit_amount_cents: input.yearlyUnitAmountCents ?? undefined,
    p_quotas: input.quotas
      ? (input.quotas as unknown as import('../generated').Json)
      : null,
    p_trial_days: input.trialDays ?? undefined,
    p_description: input.description ?? undefined,
    p_is_active: input.isActive ?? undefined,
    p_archive_reason: input.archiveReason ?? undefined,
  });
  if (error) throw error;
  return data as unknown as string;
}

// ---- Quota-kind registry management -------------------------------------

export async function listQuotaKinds(): Promise<QuotaKindRow[]> {
  const { data, error } = await supabase()
    .schema('billing')
    .from('quota_kinds')
    .select('*')
    .eq('is_active', true)
    .order('display_order')
    .limit(200);
  if (error) throw error;
  return (data ?? []) as QuotaKindRow[];
}

export async function adminListQuotaKinds(): Promise<QuotaKindRow[]> {
  const { data, error } = await supabase()
    .schema('billing')
    .rpc('rpc_admin_list_quota_kinds');
  if (error) throw error;
  return (data ?? []) as QuotaKindRow[];
}

export interface UpsertQuotaKindInput {
  key: string;
  label?: string;
  unit?: string;
  period?: 'lifetime' | 'period';
  measureKind?: 'live_clients' | 'live_stores' | 'live_seats' | 'counter';
  description?: string | null;
  displayOrder?: number;
  isActive?: boolean;
}

export async function adminUpsertQuotaKind(input: UpsertQuotaKindInput): Promise<void> {
  const { error } = await supabase().schema('billing').rpc('rpc_admin_upsert_quota_kind', {
    p_key: input.key,
    p_label: input.label ?? undefined,
    p_unit: input.unit ?? undefined,
    p_period: input.period ?? undefined,
    p_measure_kind: input.measureKind ?? undefined,
    p_description: input.description ?? undefined,
    p_display_order: input.displayOrder ?? undefined,
    p_is_active: input.isActive ?? undefined,
  });
  if (error) throw error;
}

export async function adminSetPlanStripeIds(
  id: string,
  stripeProductId: string | null,
  monthlyPriceId: string | null,
  yearlyPriceId: string | null,
): Promise<void> {
  const { error } = await supabase().schema('billing').rpc('rpc_admin_set_plan_stripe_ids', {
    p_id: id,
    p_stripe_product_id: stripeProductId ?? undefined,
    p_monthly_stripe_price_id: monthlyPriceId ?? undefined,
    p_yearly_stripe_price_id: yearlyPriceId ?? undefined,
  });
  if (error) throw error;
}

export async function adminListModulePrices(): Promise<ModulePriceRow[]> {
  const { data, error } = await supabase().schema('billing').rpc('rpc_admin_list_module_prices');
  if (error) throw error;
  return (data ?? []) as unknown as ModulePriceRow[];
}

// ---------------------------------------------------------------------------
// Plan modules + module-is-free (admin)
//
// Three RPCs (see supabase/migrations/20260525140200_billing_admin_plan_modules_rpcs.sql)
// let the Admin app re-shape which modules are included in a plan and
// flip a module's "always-on" bit without shipping a migration.
// ---------------------------------------------------------------------------

/**
 * Lists every active module plus a pre-resolved `included` boolean for
 * the given plan. Used by the BillingPlansPage "Edit plan" modal so it
 * can render the included-modules checklist next to the quotas form.
 */
export async function adminListPlanModules(planId: string): Promise<PlanModuleRow[]> {
  const { data, error } = await supabase()
    .schema('billing')
    .rpc('rpc_admin_list_plan_modules', { p_plan_id: planId });
  if (error) throw error;
  return (data ?? []) as unknown as PlanModuleRow[];
}

/**
 * Replaces the plan's plan_modules rows with exactly `moduleIds`. The
 * RPC recomputes org_module_flags for every org currently on the plan
 * in the same transaction so the operator sees the entitlement change
 * land immediately.
 */
export async function adminSetPlanModules(
  planId: string,
  moduleIds: string[],
): Promise<void> {
  const { error } = await supabase()
    .schema('billing')
    .rpc('rpc_admin_set_plan_modules', {
      p_plan_id: planId,
      p_module_ids: moduleIds,
    });
  if (error) throw error;
}

/**
 * Flips billing.module_prices.is_free and recomputes flags for every
 * org. Wired into the BillingModulePricingPage "Free module" checkbox
 * so toggling it actually grants/revokes the entitlement (rather than
 * just zeroing the Stripe Price like the old behavior did).
 */
export async function adminSetModuleIsFree(
  moduleId: string,
  isFree: boolean,
): Promise<void> {
  const { error } = await supabase()
    .schema('billing')
    .rpc('rpc_admin_set_module_is_free', {
      p_module_id: moduleId,
      p_is_free: isFree,
    });
  if (error) throw error;
}

export interface UpsertModulePriceInput {
  moduleId: string;
  name?: string;
  monthlyUnitAmountCents?: number;
  yearlyUnitAmountCents?: number;
  description?: string | null;
  displayOrder?: number;
  isActive?: boolean;
  archiveReason?: string | null;
}

export async function adminUpsertModulePrice(input: UpsertModulePriceInput): Promise<void> {
  const { error } = await supabase().schema('billing').rpc('rpc_admin_upsert_module_price', {
    p_module_id: input.moduleId,
    p_name: input.name ?? undefined,
    p_monthly_unit_amount_cents: input.monthlyUnitAmountCents ?? undefined,
    p_yearly_unit_amount_cents: input.yearlyUnitAmountCents ?? undefined,
    p_description: input.description ?? undefined,
    p_display_order: input.displayOrder ?? undefined,
    p_is_active: input.isActive ?? undefined,
    p_archive_reason: input.archiveReason ?? undefined,
  });
  if (error) throw error;
}

export async function adminSetModuleStripeIds(
  moduleId: string,
  stripeProductId: string | null,
  monthlyPriceId: string | null,
  yearlyPriceId: string | null,
): Promise<void> {
  const { error } = await supabase().schema('billing').rpc('rpc_admin_set_module_stripe_ids', {
    p_module_id: moduleId,
    p_stripe_product_id: stripeProductId ?? undefined,
    p_monthly_stripe_price_id: monthlyPriceId ?? undefined,
    p_yearly_stripe_price_id: yearlyPriceId ?? undefined,
  });
  if (error) throw error;
}

export interface SetQuotaOverrideInput {
  orgId: string;
  quotaKind: QuotaKind;
  value: number;
  expiresAt?: string | null;
  reason?: string | null;
}

export async function adminSetQuotaOverride(input: SetQuotaOverrideInput): Promise<void> {
  const { error } = await supabase().schema('billing').rpc('rpc_admin_set_quota_override', {
    p_org_id: input.orgId,
    p_quota_kind: input.quotaKind,
    p_value: input.value,
    p_expires_at: input.expiresAt ?? undefined,
    p_reason: input.reason ?? undefined,
  });
  if (error) throw error;
}

export async function adminClearQuotaOverride(orgId: string, quotaKind: QuotaKind): Promise<void> {
  const { error } = await supabase().schema('billing').rpc('rpc_admin_clear_quota_override', {
    p_org_id: orgId,
    p_quota_kind: quotaKind,
  });
  if (error) throw error;
}

export interface SetEntitlementOverrideInput {
  orgId: string;
  moduleId: string;
  enabled: boolean;
  expiresAt?: string | null;
  reason?: string | null;
}

export async function adminSetEntitlementOverride(input: SetEntitlementOverrideInput): Promise<void> {
  const { error } = await supabase().schema('billing').rpc('rpc_admin_set_entitlement_override', {
    p_org_id: input.orgId,
    p_module_id: input.moduleId,
    p_enabled: input.enabled,
    p_expires_at: input.expiresAt ?? undefined,
    p_reason: input.reason ?? undefined,
  });
  if (error) throw error;
}

/**
 * Platform-admin-only override that flips
 * `billing.org_subscriptions.dev_paid_override` for the given org.
 *
 * When `enabled=true`:
 *   - `fn_sync_org_module_flags` treats the org as paid-eligible regardless
 *     of subscription status / trial_end, lighting up plan + add-on modules.
 *   - The trial-expiry cron leaves the row alone (won't flip to past_due).
 *   - If the messaging module is on, a managed Sendblue subaccount is
 *     queued via `core.rpc_sendblue_request_subaccount` -- the same path
 *     the Stripe webhook takes on a real `→ active` transition. The
 *     pg_cron sweeper drains the queue every 15 minutes, or you can
 *     invoke the worker manually via the Settings → Messaging page.
 *
 * When `enabled=false`:
 *   - The row reverts to its real Stripe-driven status. Cancel any managed
 *     Sendblue subaccount separately via Admin if you don't want the line
 *     to keep accruing cost.
 *
 * The migration (20260628100000_billing_dev_paid_override.sql) wires every
 * downstream consumer; nothing else needs to know about the flag.
 */
export interface SetDevPaidOverrideInput {
  orgId: string;
  enabled: boolean;
  /** Required when `enabled=true`; ignored otherwise. Free-form text shown
   * in the audit trail (`core.system_events`). */
  reason?: string | null;
}

export async function adminSetDevPaidOverride(input: SetDevPaidOverrideInput): Promise<void> {
  // The new RPC is not yet in `generated.ts` (next `supabase gen types`
  // pass will pick it up). Cast around the typed RPC table the same way
  // `getOrgBilling` does for `rpc_org_get_billing`.
  const client = supabase().schema('billing') as unknown as {
    rpc(
      fn: 'rpc_admin_set_dev_paid_override',
      args: { p_org_id: string; p_enabled: boolean; p_reason?: string | null },
    ): Promise<{ data: unknown; error: unknown }>;
  };
  const { error } = await client.rpc('rpc_admin_set_dev_paid_override', {
    p_org_id: input.orgId,
    p_enabled: input.enabled,
    p_reason: input.reason ?? undefined,
  });
  if (error) throw error;
}

export async function adminGetOrgBilling(orgId: string): Promise<OrgBillingSnapshot> {
  const { data, error } = await supabase().schema('billing').rpc('rpc_admin_get_org_billing', {
    p_org_id: orgId,
  });
  if (error) throw error;
  return data as unknown as OrgBillingSnapshot;
}

export async function adminListSubscriptions(): Promise<AdminSubscriptionRow[]> {
  const { data, error } = await supabase().schema('billing').rpc('rpc_admin_list_subscriptions');
  if (error) throw error;
  return (data ?? []) as AdminSubscriptionRow[];
}

// ---- operator-app convenience -------------------------------------------

function readActiveOrgIdFromStorage(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem('pc.activeOrgId');
    if (!raw || raw.trim().length === 0) return null;
    return raw;
  } catch {
    return null;
  }
}

export async function getOrgBilling(): Promise<OrgBillingSnapshot> {
  const activeOrgId = readActiveOrgIdFromStorage();
  const client = supabase().schema('billing') as unknown as {
    rpc(
      fn: 'rpc_org_get_billing',
      args?: { p_org_id?: string },
    ): Promise<{ data: unknown; error: unknown }>;
  };
  const { data, error } = await client.rpc(
    'rpc_org_get_billing',
    activeOrgId ? { p_org_id: activeOrgId } : undefined,
  );
  if (error) throw error;
  return data as unknown as OrgBillingSnapshot;
}

// ---- error helper --------------------------------------------------------

/**
 * Quota-exceeded errors raised by billing.fn_assert_quota carry Postgres
 * SQLSTATE 'PC001' plus a JSON `hint` describing the offending quota. This
 * helper surfaces that detail so the operator app can render an upgrade
 * modal instead of a generic error message.
 */
export interface QuotaError {
  kind: 'quota';
  quota: QuotaKind;
  /** Friendly label for the quota, sourced from billing.quota_kinds.label when available. */
  label: string;
  cap: number;
  current: number;
  delta: number;
  message: string;
}

export function parseQuotaError(e: unknown): QuotaError | null {
  if (!e || typeof e !== 'object') return null;
  const err = e as Record<string, unknown>;
  if (err.code !== 'PC001') return null;
  const hintRaw = typeof err.hint === 'string' ? err.hint : null;
  let parsed: {
    quota?: string;
    label?: string;
    cap?: number;
    current?: number;
    delta?: number;
  } = {};
  if (hintRaw) {
    try {
      parsed = JSON.parse(hintRaw) as typeof parsed;
    } catch {
      parsed = {};
    }
  }
  const quota = parsed.quota ?? 'clients';
  return {
    kind: 'quota',
    quota,
    label: parsed.label ?? quota,
    cap: parsed.cap ?? 0,
    current: parsed.current ?? 0,
    delta: parsed.delta ?? 1,
    message: typeof err.message === 'string' ? err.message : 'Quota exceeded.',
  };
}

// ---- Edge Function wrappers ---------------------------------------------

export interface CheckoutInput {
  orgId: string;
  planId: string;
  billingCycle: BillingCycle;
  moduleIds: string[];
  successUrl: string;
  cancelUrl: string;
}

/**
 * Discriminated result from `stripe-billing-checkout`.
 *
 *   * `mode: 'checkout'` -- caller should redirect to `url`. Used for
 *     first-time subscription signup, or when the org's previous
 *     subscription is canceled/expired and a fresh card capture is needed.
 *
 *   * `mode: 'updated'` -- no redirect. The function reconciled the
 *     existing live Stripe subscription in place; the webhook will fire
 *     `customer.subscription.updated` and the billing snapshot should be
 *     refetched. `no_change` is true when the operator clicked "Update"
 *     without changing the plan/cycle/modules selection.
 *
 *   * legacy field `url` is also populated when `mode === 'checkout'` so
 *     older callers that only check `.url` keep working.
 */
export interface CheckoutResult {
  ok: boolean;
  mode?: 'checkout' | 'updated';
  url?: string;
  session_id?: string;
  subscription_id?: string;
  no_change?: boolean;
  error?: string;
}

/**
 * supabase-js `functions.invoke` returns a generic `FunctionsHttpError` on any
 * non-2xx response whose `.message` is literally "Edge Function returned a
 * non-2xx status code", swallowing whatever JSON body our function returned.
 * The real error message is on `error.context` (the raw `Response`). This
 * helper pulls it out so the operator sees something actionable instead of
 * the supabase-js wrapper string.
 */
async function readEdgeError(error: unknown, fallback: string): Promise<Error> {
  if (error && typeof error === 'object' && 'context' in error) {
    const ctx = (error as { context: unknown }).context;
    if (ctx instanceof Response) {
      try {
        const cloned = ctx.clone();
        const text = await cloned.text();
        if (text) {
          try {
            const body = JSON.parse(text) as { error?: string; message?: string };
            const msg = body.error ?? body.message;
            if (msg) return new Error(msg);
          } catch {
            return new Error(text);
          }
        }
      } catch {
        // fall through to fallback
      }
    }
  }
  if (error instanceof Error) return error;
  return new Error(fallback);
}

export async function startCheckoutSession(input: CheckoutInput): Promise<CheckoutResult> {
  const { data, error } = await supabase().functions.invoke('stripe-billing-checkout', {
    body: {
      org_id: input.orgId,
      plan_id: input.planId,
      billing_cycle: input.billingCycle,
      module_ids: input.moduleIds,
      success_url: input.successUrl,
      cancel_url: input.cancelUrl,
    },
  });
  if (error) throw await readEdgeError(error, 'Checkout failed.');
  const result = data as CheckoutResult;
  if (!result.ok) throw new Error(result.error ?? 'Checkout failed.');
  return result;
}

export async function openCustomerPortal(orgId: string, returnUrl: string): Promise<string> {
  const { data, error } = await supabase().functions.invoke('stripe-billing-portal', {
    body: { org_id: orgId, return_url: returnUrl },
  });
  if (error) throw await readEdgeError(error, 'Failed to open customer portal.');
  const result = data as { ok: boolean; url?: string; error?: string };
  if (!result.ok || !result.url) {
    throw new Error(result.error ?? 'Failed to open customer portal.');
  }
  return result.url;
}

// ---- admin Edge Function: single mutation surface -----------------------

export interface AdminActionResult {
  ok: boolean;
  error?: string;
  [k: string]: unknown;
}

async function invokeAdmin(action: string, args: Record<string, unknown>): Promise<AdminActionResult> {
  const { data, error } = await supabase().functions.invoke('stripe-billing-admin', {
    body: { action, ...args },
  });
  if (error) throw await readEdgeError(error, `admin action ${action} failed`);
  const r = data as AdminActionResult;
  if (!r.ok) throw new Error(r.error ?? `admin action ${action} failed`);
  return r;
}

export interface EditPlanPriceInput {
  planId: string;
  cycle: BillingCycle;
  newAmountCents: number;
  proration?: 'next_renewal' | 'immediate';
}

export function adminEditPlanPrice(input: EditPlanPriceInput) {
  return invokeAdmin('edit_plan_price', {
    plan_id: input.planId,
    cycle: input.cycle,
    new_amount_cents: input.newAmountCents,
    proration: input.proration ?? 'next_renewal',
  });
}

export interface EditModulePriceInput {
  moduleId: string;
  cycle: BillingCycle;
  newAmountCents: number;
  proration?: 'next_renewal' | 'immediate';
}

export function adminEditModulePrice(input: EditModulePriceInput) {
  return invokeAdmin('edit_module_price', {
    module_id: input.moduleId,
    cycle: input.cycle,
    new_amount_cents: input.newAmountCents,
    proration: input.proration ?? 'next_renewal',
  });
}

/**
 * Single-call save + Stripe reconciliation for a module add-on. Handles
 * first-time setup (creates the Stripe Product + Prices) and routine edits
 * (rotates the Prices, archives the old ones, migrates active subscription
 * items) in one Edge Function invocation. The admin UI calls this on every
 * save so the operator never has to run the bootstrap script for routine
 * pricing changes.
 */
export interface SyncModulePricingInput {
  moduleId: string;
  monthlyUnitAmountCents: number;
  yearlyUnitAmountCents: number;
  name?: string;
  description?: string | null;
  displayOrder?: number;
  isActive?: boolean;
  proration?: 'next_renewal' | 'immediate';
}

export interface SyncPricingResult extends AdminActionResult {
  stripe_product_id?: string;
  monthly_stripe_price_id?: string | null;
  yearly_stripe_price_id?: string | null;
}

export async function adminSyncModulePricing(
  input: SyncModulePricingInput,
): Promise<SyncPricingResult> {
  return (await invokeAdmin('sync_module_pricing', {
    module_id: input.moduleId,
    monthly_unit_amount_cents: input.monthlyUnitAmountCents,
    yearly_unit_amount_cents: input.yearlyUnitAmountCents,
    ...(input.name !== undefined ? { name: input.name } : {}),
    ...(input.description !== undefined ? { description: input.description } : {}),
    ...(input.displayOrder !== undefined ? { display_order: input.displayOrder } : {}),
    ...(input.isActive !== undefined ? { is_active: input.isActive } : {}),
    proration: input.proration ?? 'next_renewal',
  })) as SyncPricingResult;
}

/**
 * Single-call save + Stripe reconciliation for a plan. Same shape as the
 * module variant; additionally accepts the full quota cap array so price +
 * caps + display name go through one round trip.
 */
export interface SyncPlanPricingInput {
  planId: string;
  monthlyUnitAmountCents: number;
  yearlyUnitAmountCents: number;
  name?: string;
  description?: string | null;
  quotas?: PlanQuotaCap[];
  isActive?: boolean;
  proration?: 'next_renewal' | 'immediate';
}

export async function adminSyncPlanPricing(
  input: SyncPlanPricingInput,
): Promise<SyncPricingResult> {
  return (await invokeAdmin('sync_plan_pricing', {
    plan_id: input.planId,
    monthly_unit_amount_cents: input.monthlyUnitAmountCents,
    yearly_unit_amount_cents: input.yearlyUnitAmountCents,
    ...(input.name !== undefined ? { name: input.name } : {}),
    ...(input.description !== undefined ? { description: input.description } : {}),
    ...(input.quotas !== undefined ? { quotas: input.quotas } : {}),
    ...(input.isActive !== undefined ? { is_active: input.isActive } : {}),
    proration: input.proration ?? 'next_renewal',
  })) as SyncPricingResult;
}

export interface CreateEnterprisePlanInput {
  orgId: string;
  name: string;
  monthlyUnitAmountCents: number;
  yearlyUnitAmountCents: number;
  /** Per-quota cap array. The keys must already exist in billing.quota_kinds. */
  quotas: PlanQuotaCap[];
  description?: string | null;
}

export function adminCreateEnterprisePlan(input: CreateEnterprisePlanInput) {
  return invokeAdmin('create_enterprise_plan', {
    org_id: input.orgId,
    name: input.name,
    monthly_unit_amount_cents: input.monthlyUnitAmountCents,
    yearly_unit_amount_cents: input.yearlyUnitAmountCents,
    quotas: input.quotas,
    description: input.description ?? undefined,
  });
}

export interface SetPercentOverrideInput {
  orgId: string;
  targetKind: PriceOverrideTarget;
  moduleId?: string | null;
  percentOff: number;
  duration?: 'forever' | 'once' | 'repeating';
  durationInMonths?: number | null;
  effectiveUntil?: string | null;
  notes?: string | null;
}

export function adminSetPercentOverride(input: SetPercentOverrideInput) {
  return invokeAdmin('set_percent_override', {
    org_id: input.orgId,
    target_kind: input.targetKind,
    module_id: input.moduleId ?? undefined,
    percent_off: input.percentOff,
    duration: input.duration ?? 'forever',
    duration_in_months: input.durationInMonths ?? undefined,
    effective_until: input.effectiveUntil ?? undefined,
    notes: input.notes ?? undefined,
  });
}

export interface SetFixedOverrideInput {
  orgId: string;
  targetKind: PriceOverrideTarget;
  moduleId?: string | null;
  customMonthlyUnitAmountCents?: number | null;
  customYearlyUnitAmountCents?: number | null;
  effectiveUntil?: string | null;
  notes?: string | null;
}

export function adminSetFixedOverride(input: SetFixedOverrideInput) {
  return invokeAdmin('set_fixed_override', {
    org_id: input.orgId,
    target_kind: input.targetKind,
    module_id: input.moduleId ?? undefined,
    custom_monthly_unit_amount_cents: input.customMonthlyUnitAmountCents ?? undefined,
    custom_yearly_unit_amount_cents: input.customYearlyUnitAmountCents ?? undefined,
    effective_until: input.effectiveUntil ?? undefined,
    notes: input.notes ?? undefined,
  });
}

export function adminClearPriceOverride(overrideId: string) {
  return invokeAdmin('clear_override', { override_id: overrideId });
}

export function adminExtendTrial(orgId: string, newTrialEnd: string) {
  return invokeAdmin('extend_trial', { org_id: orgId, new_trial_end: newTrialEnd });
}

export function adminCancelSubscription(orgId: string, atPeriodEnd = true) {
  return invokeAdmin('cancel_subscription', { org_id: orgId, at_period_end: atPeriodEnd });
}

export function adminRefundInvoice(invoiceId: string, amountCents?: number) {
  return invokeAdmin('refund_invoice', {
    invoice_id: invoiceId,
    ...(amountCents ? { amount_cents: amountCents } : {}),
  });
}

// ---- formatting helpers (used by both admin and operator UIs) ------------

export function formatCents(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

export function effectiveMonthlyCents(plan: PlanRow, cycle: BillingCycle): number {
  return cycle === 'monthly'
    ? plan.monthly_unit_amount_cents
    : Math.round(plan.yearly_unit_amount_cents / 12);
}

export function effectiveMonthlyForModule(price: ModulePriceRow, cycle: BillingCycle): number {
  return cycle === 'monthly'
    ? price.monthly_unit_amount_cents
    : Math.round(price.yearly_unit_amount_cents / 12);
}
